/** Bound retained chart images so a large valid notebook remains downloadable. */
const MAX_CHART_CAPTURE_PIXELS = 16_000_000

export type NotebookChartImages = Record<string, string | null>

/**
 * Capture only rendered chart SVGs; no HTML screenshot or remote image request.
 * With `skipFailed`, a chart that has not rendered or cannot be rasterized is
 * left out instead of failing the whole capture.
 */
export async function notebookChartImages(
  root: HTMLElement,
  { skipFailed = false }: { skipFailed?: boolean } = {},
): Promise<NotebookChartImages> {
  const images: NotebookChartImages = {}
  let capturedPixels = 0
  for (const element of root.querySelectorAll<HTMLElement>(
    "[data-notebook-chart]",
  )) {
    const svg = element.querySelector<SVGSVGElement>("svg.recharts-surface")
    const id = element.dataset.notebookChart
    if (!svg || !id) {
      continue
    }
    // Reused evidence has one PDF image key; capture it once so a later copy
    // cannot consume the budget or replace a good image with a null fallback.
    if (id in images) {
      continue
    }
    const copy = svg.cloneNode(true) as SVGSVGElement
    const originalNodes = [svg, ...svg.querySelectorAll("*")]
    const copiedNodes = [copy, ...copy.querySelectorAll("*")]
    originalNodes.forEach((node, index) => {
      const computed = window.getComputedStyle(node)
      for (const property of [
        "fill",
        "stroke",
        "stroke-width",
        "opacity",
        "font-size",
        "font-family",
        "font-weight",
      ]) {
        copiedNodes[index].setAttribute(
          property,
          computed.getPropertyValue(property),
        )
      }
    })
    const { width, height } = svg.getBoundingClientRect()
    if ((!width || !height) && skipFailed) {
      continue
    }
    if (!width || !height) {
      throw new Error(
        "A chart is still loading. Wait for it to render before downloading.",
      )
    }
    const pixels = Math.ceil(width) * Math.ceil(height)
    if (capturedPixels + pixels > MAX_CHART_CAPTURE_PIXELS) {
      // The PDF still includes the complete source table for this chart.
      images[id] = null
      continue
    }
    copy.setAttribute("width", String(width))
    copy.setAttribute("height", String(height))
    copy.setAttribute("xmlns", "http://www.w3.org/2000/svg")
    const blob = new Blob([new XMLSerializer().serializeToString(copy)], {
      type: "image/svg+xml",
    })
    const url = URL.createObjectURL(blob)
    try {
      images[id] = await rasterize(url, width, height)
      capturedPixels += pixels
    } catch (failure) {
      if (!skipFailed) {
        throw failure
      }
    } finally {
      URL.revokeObjectURL(url)
    }
  }
  return images
}

async function rasterize(url: string, width: number, height: number) {
  const image = new Image()
  image.src = url
  await image.decode()
  const canvas = document.createElement("canvas")
  canvas.width = Math.ceil(width)
  canvas.height = Math.ceil(height)
  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("Could not prepare the notebook charts.")
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height)
  return canvas.toDataURL("image/png")
}
